import { useRef, useState } from 'react';
import ImageSearchResultList from './ImageSearchResultList';
import ImageSearchResultDetail from './ImageSearchResultDetail';
import type { ImageSearchResponseType, ImageSearchResultType } from '../../types/type';
import { useImageSearchResultItemActions } from '../../hooks/useImageSearchResultItemActions';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import type { UpsertPhotoCardsToCollectRequestType } from '../../hooks/useUpsertPhotoCardsToCollectionMutation';
import { useUpsertPhotoCardsToCollectionMutation } from '../../hooks/useUpsertPhotoCardsToCollectionMutation';
import { useNavigate } from 'react-router-dom';
import { useSelectedTradingCardsStore } from '../../../trading/store/useSelectedTradingCardsStore';
import useUserQuery from '../../../hooks/queries/useUserQuery';
import { formatPhotoCardAsTradingCard } from '../../utils/formatPhotoCardAsTradingCard';
import { useSuccessPhotoCardToast } from '../../../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { t } from 'i18next';

const STEPS = {
  LIST: 'LIST',
  DETAIL: 'DETAIL'
} as const;

const ImageSearchSuccessContainer = ({
  onBackClick,
  selectedMember
}: {
  onBackClick: () => void;
  selectedMember: ImageSearchResultType['selectedMember'];
}) => {
  const [step, setStep] = useState<string>(STEPS.LIST);
  const [selectedResultItem, setSelectedResultItem] = useState<
    ImageSearchResponseType['results'][number] | null
  >(null);
  const listScrollPositionRef = useRef<number>(0);
  const navigate = useNavigate();
  const showSuccessToast = useSuccessPhotoCardToast();

  const { addPhotoCard, clearPhotoCards } = useSelectedTradingCardsStore();
  const { data: user } = useUserQuery();
  const { mutateAsync: upsertToCollection, isLoading: isUpsertingToCollection } =
    useUpsertPhotoCardsToCollectionMutation();
  const { selectResultCandidate, deleteResultItem } = useImageSearchResultItemActions();

  if (!user) {
    return null;
  }

  const onAddToMyPhotoCardClick = async (searchResult: ImageSearchResponseType) => {
    await upsertToCollection({
      artistId: selectedMember.artistId,
      memberId: selectedMember.memberId,
      photoCards: getPhotoCardsForUpsert(searchResult)
    });
    navigate('/collections');
    showSuccessToast('top', t('imageSearch.success.addCollectionSuccess') as string);
  };

  const onSellClick = (searchResult: ImageSearchResponseType) => {
    clearPhotoCards();
    for (const result of searchResult.results) {
      if (!result.candidates[0].photoCard) {
        continue;
      }
      const photoCard = formatPhotoCardAsTradingCard(result.candidates[0].photoCard, user);
      addPhotoCard(photoCard);
    }
    openNewTab('/new-my-trading-photo-cards/register-sale?new=true');
  };

  return (
    <>
      {step === STEPS.LIST && (
        <ImageSearchResultList
          isLoading={isUpsertingToCollection}
          listScrollStartPosition={listScrollPositionRef.current}
          onDeleteClick={(position) => {
            deleteResultItem(position);
            showSuccessToast('top', t('imageSearch.success.deletedList') as string);
          }}
          onBackClick={onBackClick}
          onOpenCandidateList={(resultItem) => {
            setStep(STEPS.DETAIL);
            setSelectedResultItem(resultItem);
          }}
          onScrollChange={(scrollPosition) => {
            listScrollPositionRef.current = scrollPosition;
          }}
          onCardClick={(photoCardId) => {
            openNewTab(`/photo_cards/${photoCardId}`);
          }}
          onSellClick={onSellClick}
          onAddToMyPhotoCardClick={onAddToMyPhotoCardClick}
        />
      )}
      {step === STEPS.DETAIL && selectedResultItem && (
        <ImageSearchResultDetail
          onCardClick={({ position, photoCardId }) => {
            setStep(STEPS.LIST);
            selectResultCandidate({ position, photoCardId });
            showSuccessToast('top', t('imageSearch.success.selectedPhotoCard') as string);
          }}
          onBackClick={() => {
            setStep(STEPS.LIST);
            setSelectedResultItem(null);
          }}
          selectedMember={selectedMember}
          searchResult={selectedResultItem}
        />
      )}
    </>
  );
};

export default ImageSearchSuccessContainer;

/**
 * 이미지 검색 결과를 컬렉션 업데이트 요청 형태로 변환
 */
const getPhotoCardsForUpsert = (
  selectedResultItem: ImageSearchResponseType
): UpsertPhotoCardsToCollectRequestType['photoCards'] => {
  /**
   * 1. 기존 보유 개수 + 1
   * 2. 중복 카드는 개수 누적
   */
  const cardMap = selectedResultItem.results.reduce(
    (acc, result) => {
      const photoCardId = result.candidates[0].photoCardId;

      // 1. 기존 카드 보유 횟수에서 + 1
      const collectedCount = (result.candidates[0].photoCard?.collectedCount ?? 0) + 1;

      // 2. 중복 카드는 개수 누적
      if (acc[photoCardId]) {
        // 요청 배열안에 같은 카드가 존재하면 요청 숫자 1 증가
        acc[photoCardId].collected_count += 1;
      } else {
        acc[photoCardId] = {
          photo_card_id: photoCardId,
          collected_count: collectedCount
        };
      }
      return acc;
    },
    {} as Record<string, { photo_card_id: string; collected_count: number }>
  );

  return Object.values(cardMap);
};
